<template>
  <!-- W9 §1: the graph's own wrapper, so CSS can give the chart a size. Without it the tile's
       flex column shrinks to the content and PrimeVue's Chart falls back to the canvas
       element's 300 x 150 default. -->
  <div class="dashboard-graph">
    <ProgressSpinner
      v-if="loadingData"
      style="width:25px; height:25px; width: 100%;" 
      strokeWidth="5" 
      fill="#EEEEEE" 
      animationDuration=".5s"
    />
    <template v-else>
      <div v-if="datesNote" class="dashboard-dates-note">Dates do not apply</div>
      <!-- Clicking a bar opens report 12 for that month (item 4, decision 2a). The chart's
           labels are January..December, so the bar's index is its month. -->
      <Chart 
      type="bar" 
      :data="chartData" 
      :options="chartOptions"
      @select="onBarSelect"
      />
    </template>
  </div>
</template>

<script>
import UserService from "@/service/UserService";
import { barAppliesTo, barDimensionParams, hasDate, withQuery } from './dashboardFilterBar'
import { graphDrillTarget, hasDrill } from './dashboardDrill'

export default {
  created() {
    this.setupComponent()
  },
  props: {
    widget: {
      type: Object,
      required: false
    },
    // The dashboard filter bar; its four dimensions go to the endpoint (dates not yet).
    filterBar: {
      type: Object,
      default: null
    },
    // What a bar opens: { reportId, drill }, built by the backend from the filters the graph's
    // own figures ran with (v1/getUserWidgetDrills).
    drill: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    userService: new UserService(),
    loadingData: false,
    chartData: {},
    chartOptions: {
      responsive: true,
      // W9 §1. Chart.js keeps its opening 2:1 ratio unless told not to, which would make a
      // half-width graph half as tall; with this off it fills the height the CSS gives the
      // container, and `responsive` is what answers a window resize and the W3 narrower/wider
      // controls - Chart.js's own handler, no private API.
      maintainAspectRatio: false,
      title: {
        display: true,
        text: 'Sales Analysis'
      },
      tooltips: {
        mode: 'index',
        intersect: true,
        callbacks: {
                    label: function(context, dataset) {
                      var l = dataset.datasets[context.datasetIndex].label
                      var b = context.yLabel
                      b = b.toLocaleString('en-GB', {minimumFractionDigits: 2, maximumFractionDigits: 2})
                      return l + ': ' + '£'+b;
                    }
                  }
      },
      scales: {
              yAxes: [
                  {
                      ticks: {
                          callback: function(label, index, labels) {
                              return '£'+label.toLocaleString();
                          }
                      }
                  }
        ]
      },
    },
    userId: null
  }),
  computed: {
    reports() {
      return (this.$store && this.$store.getters['reports/getAllReports']) || []
    },
    drillable() {
      return hasDrill(this.drill, this.reports)
    },
    datesNote() {
      return !!(this.widget && this.widget.widget && barAppliesTo(this.widget.widget.widgetEP) && hasDate(this.filterBar))
    },
    profile() {
      return this.$store.getters['auth/getMyProfile']
    }
  },
  watch: {
    filterBar(val, old) {
      if (JSON.stringify(barDimensionParams(val)) !== JSON.stringify(barDimensionParams(old))) {
        this.setupComponent()
      }
    },
    profile(val) {
      if (val && val.id) {
        this.setupcomponent()
      }
    }
  },
  methods: {
    // PrimeVue's Chart reports the clicked element; its index is the month, because the labels
    // are the twelve months in order. A click on empty chart space carries no element.
    onBarSelect(event) {
      const index = event && event.element ? event.element._index : null
      if (index === null || index === undefined) return
      const target = graphDrillTarget(this.drill, index + 1, this.reports)
      if (target) this.$router.push(target)
    },
    setupComponent() {
      if (this.$route.params.userid) {       
        this.userId = this.$route.params.userid
      } else if (this.profile && this.profile.id) {
        this.userId = this.$store.getters['auth/getMyProfile'].id
      }


      if (this.widget && this.$store.getters['auth/getMyProfile']) {
        if (this.widget.widget) {
          if (this.widget.widget.widgetEP) {
            this.loadingData = true

            let path = this.widget.widget.widgetEP.replace('{userId}', this.userId)
            if (barAppliesTo(this.widget.widget.widgetEP)) {
              path = withQuery(path, barDimensionParams(this.filterBar))
            }

            this.userService
              .getCustomUserWidgetData(path)
              .then(data => {
                this.loadingData = false

                if (data && data.datasets && data.labels) {
                  let a = {...data}
                  
                  a.datasets[0].data = a.datasets[0].dataVal
                  a.datasets[1].data = a.datasets[1].dataVal
                  a.datasets[2].data = a.datasets[2].dataVal

                  this.chartData = a
                }
              })
              .catch(error => {
                this.loadingData = false

                this.$store.dispatch('addErrorMessage', {
                  severity: 'error',
                  content: 'Some data was not loaded from "' + path + '"'
                })
              })
          }
        }
      }
    }             
  }
}
</script>

<style>
/* W9 §1. The height lives here, once: `--dashboard-graph-height`. A half-width graph keeps the
   same height as a full-width one, which is why the aspect ratio is off in chartOptions.
   Not scoped, because the element being sized is PrimeVue's own `.p-chart` inside Chart.vue. */
.dashboard-graph {
  --dashboard-graph-height: 360px;
  width: 100%;
  min-width: 0;
}
.dashboard-graph .p-chart {
  width: 100%;
  height: var(--dashboard-graph-height);
}
</style>